/**
 * Social Authentication Service
 * Handles Google and Facebook authentication using Firebase
 * Matches mobile app implementation and uses the same backend API
 */

import {
  signInWithPopup,
  signInWithCredential,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  UserCredential,
} from "firebase/auth";
import { auth, googleProvider, facebookProvider } from "@/lib/firebase/config";
import { API_BASE_URL } from "./api-config";
import { normalizeLocale } from "@/lib/utils";
import { LoginResponse, UserData } from "./auth-service";
import axios from "axios";

export enum SocialProvider {
  GOOGLE = 2,
  FACEBOOK = 3,
  APPLE = 4, // For future use
}

export interface SocialLoginRequest {
  provider: SocialProvider;
  role_id: number; // 2 for client, 3 for trainer
  token: string; // Firebase ID token
}

export interface SocialLoginResponse {
  token: string;
  firebase_token?: string;
  user_data: UserData;
}

/**
 * Sign in with Google using Firebase
 */
export async function signInWithGoogle(): Promise<UserCredential> {
  try {
    const result = await signInWithPopup(auth(), googleProvider());
    return result;
  } catch (error: any) {
    console.error("Google sign-in error:", error);
    if (error.code === "auth/popup-closed-by-user") {
      throw new Error("Sign-in popup was closed. Please try again.");
    }
    if (error.code === "auth/popup-blocked") {
      throw new Error("Popup was blocked. Please allow popups and try again.");
    }
    throw new Error(error.message || "Google sign-in failed. Please try again.");
  }
}

/**
 * Sign in with Facebook using Firebase
 */
export async function signInWithFacebook(): Promise<UserCredential> {
  try {
    const result = await signInWithPopup(auth(), facebookProvider());
    return result;
  } catch (error: any) {
    console.error("Facebook sign-in error:", error);
    if (error.code === "auth/popup-closed-by-user") {
      throw new Error("Sign-in popup was closed. Please try again.");
    }
    if (error.code === "auth/popup-blocked") {
      throw new Error("Popup was blocked. Please allow popups and try again.");
    }
    if (error.code === "auth/account-exists-with-different-credential") {
      throw new Error(
        "An account already exists with the same email address but different sign-in credentials."
      );
    }
    throw new Error(error.message || "Facebook sign-in failed. Please try again.");
  }
}

/**
 * Get Firebase ID token from user credential
 */
async function getIdToken(userCredential: UserCredential): Promise<string> {
  try {
    const idToken = await userCredential.user.getIdToken();
    return idToken;
  } catch (error: any) {
    console.error("Error getting ID token:", error);
    throw new Error("Failed to get authentication token. Please try again.");
  }
}

/**
 * Send social login request to backend API
 * Same endpoint as mobile app: POST /api/social-login
 */
async function sendSocialLoginToBackend(
  request: SocialLoginRequest
): Promise<SocialLoginResponse> {
  try {
    const language =
      typeof window !== "undefined"
        ? normalizeLocale(localStorage.getItem("language") || navigator.language || "en")
        : "en";

    const response = await axios.post<{
      status: string;
      code: number;
      message: string;
      data: {
        user: {
          id: number;
          name: string;
          email: string;
          email_verified_at?: string;
          is_verified: boolean;
          is_active: number;
          role_id: number;
          token: string;
          firebase_token?: string;
          is_profile_completed?: boolean;
          is_approved?: number;
          profile_image?: string;
          has_payment_info?: boolean;
          has_active_subscription?: boolean;
          profile_status_message?: string;
        };
      };
    }>(
      `${API_BASE_URL}/social-login`,
      {
        provider: request.provider,
        role_id: request.role_id,
      },
      {
        headers: {
          "Content-Type": "application/json",
          "Accept-Language": language,
          Authorization: `Bearer ${request.token}`,
        },
      }
    );

    if (!response.data || !response.data.data || !response.data.data.user) {
      console.error("Invalid social login response:", response.data);
      throw new Error("Invalid response structure: missing data.user field");
    }

    const userData = response.data.data.user;
    
    // Map backend user structure to UserData interface
    const userDataMapped: UserData = {
      id: userData.id,
      name: userData.name,
      email: userData.email,
      role_id: userData.role_id,
      is_active: userData.is_active === 1,
      is_verified: userData.is_verified,
      is_profile_completed: userData.is_profile_completed ?? false,
      is_approved: userData.is_approved !== undefined ? (userData.is_approved === 1) : undefined,
      profile_image: userData.profile_image,
      profile_status_message: userData.profile_status_message,
    };

    const loginData: SocialLoginResponse = {
      token: userData.token,
      firebase_token: userData.firebase_token,
      user_data: userDataMapped,
    };

    // Store token in localStorage
    if (loginData.token) {
      localStorage.setItem("token", loginData.token);
      if (loginData.firebase_token) {
        localStorage.setItem("firebase_token", loginData.firebase_token);
      }
    }

    return loginData;
  } catch (error: any) {
    console.error("Social login API error:", error);

    // Extract error message from response if available
    let errorMessage = "Social login failed. Please try again.";
    
    if (error.response?.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    // Check if it's an account already exists error
    const lowerMessage = errorMessage.toLowerCase();
    if (
      lowerMessage.includes("already exists") ||
      lowerMessage.includes("already registered") ||
      lowerMessage.includes("email already") ||
      error.response?.status === 409 || // Conflict status code
      error.response?.data?.code === 409
    ) {
      throw new Error(`Account already exists: ${errorMessage}`);
    }
    
    throw new Error(errorMessage);
  }
}

/**
 * Complete social login flow
 * 1. Sign in with provider (Google/Facebook)
 * 2. Get Firebase ID token
 * 3. Send to backend API
 * 4. Return login response
 */
export async function socialLogin(
  provider: SocialProvider,
  roleId: number
): Promise<SocialLoginResponse> {
  try {
    // Step 1: Sign in with provider
    let userCredential: UserCredential;
    if (provider === SocialProvider.GOOGLE) {
      userCredential = await signInWithGoogle();
    } else if (provider === SocialProvider.FACEBOOK) {
      userCredential = await signInWithFacebook();
    } else {
      throw new Error(`Unsupported provider: ${provider}`);
    }

    // Step 2: Get Firebase ID token
    const idToken = await getIdToken(userCredential);

    // Step 3: Send to backend API
    const loginResponse = await sendSocialLoginToBackend({
      provider,
      role_id: roleId,
      token: idToken,
    });

    return loginResponse;
  } catch (error: any) {
    console.error("Social login flow error:", error);
    throw error;
  }
}

/**
 * Sign out from Firebase
 */
export async function signOutFromFirebase(): Promise<void> {
  try {
    await auth().signOut();
  } catch (error: any) {
    console.error("Firebase sign-out error:", error);
    // Don't throw - sign out should be best effort
  }
}

